// @wc-ignore-file
// (Nothing here reaches the user: the only literals are a debug log and query
// keys, and wuchale would machine-translate both — see CLAUDE.md.)
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { recordContainerAccess } from "./container-access-api";
import { containerAccessQueries } from "./container-access-keys";

/**
 * How long one recorded access suppresses the next one for the same container.
 *
 * The sidebar orders by day-to-day recency, not by the minute, so re-stamping
 * a container the user is sitting in buys nothing and costs a write per
 * remount (route transitions, tab switches, React StrictMode's double effect).
 */
const RECORD_THROTTLE_MS = 5 * 60 * 1000;

/**
 * Per-session, per-tab: container id → when we last stamped it.
 *
 * Module-level rather than component state because the point is to survive
 * unmounts — the throttle only works if navigating away and back is the case
 * it catches. Consequence, accepted by CR-51: if two accounts are used in the
 * same tab without a reload, the second user's first open of a container the
 * first user just opened is not stamped. It self-corrects on the next open.
 */
const lastRecordedAt = new Map<string, number>();

/**
 * CR-51: record that the current user opened `containerId`.
 *
 * Fire-and-forget by design. This runs on every document / chat / message-room
 * open, so it must never be able to delay or fail a page load: errors are
 * swallowed, and nothing downstream awaits the result.
 */
export function useRecordContainerAccess(containerId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!containerId) return;

    const now = Date.now();
    const previous = lastRecordedAt.get(containerId);
    if (previous !== undefined && now - previous < RECORD_THROTTLE_MS) return;

    // Claim the slot before awaiting, so a second mount in the same tick
    // cannot race a second write through.
    lastRecordedAt.set(containerId, now);

    void recordContainerAccess(containerId)
      .then(() => {
        // The sidebar's "last 5" is now stale by exactly this row. Invalidate
        // even if this component already unmounted (StrictMode double-effect,
        // fast navigation): the query client is app-scoped, and skipping the
        // invalidation is what would leave the sidebar showing stale recency.
        void queryClient.invalidateQueries({ queryKey: containerAccessQueries.all() });
      })
      .catch((error: unknown) => {
        // Release the slot so a transient failure doesn't blank recency for
        // the whole throttle window.
        lastRecordedAt.delete(containerId);
        if (import.meta.env.DEV) {
          console.debug("record_container_access failed", error);
        }
      });
  }, [containerId, queryClient]);
}

/** Test seam: drops the per-session throttle state. */
export function resetContainerAccessThrottle(): void {
  lastRecordedAt.clear();
}
